/* Page table, path matching and the access rules every page goes through. No React here, so it's unit-tested directly. */
import { WEB_ROUTES, type Me } from '@pathabroad/core/browser';

export type PageKey = Exclude<keyof typeof WEB_ROUTES, 'planner'> | 'home' | 'notFound';

/* any:     anyone, signed in or not (legal pages, the guardian's pages, the link callback)
   landing: the home page (the globe), for visitors and signed-in students alike; a signed-in student who still has to
            type their two-step code, or is waiting for a guardian, goes there first
   guest:   for people who aren't signed in; a signed-in student is sent to their home page
   mfa:     a signed-in student who still has to type their two-step code
   minor:   a signed-in 13 to 17 year old (the consent page)
   user:    a signed-in student who can use their account */
export type Access = 'any' | 'landing' | 'guest' | 'mfa' | 'minor' | 'user';

export const PAGES: ReadonlyArray<{ key: PageKey; path: string; access: Access }> = [
  { key: 'home', path: '/', access: 'landing' },
  { key: 'start', path: WEB_ROUTES.start, access: 'guest' },
  { key: 'verifyEmail', path: WEB_ROUTES.verifyEmail, access: 'guest' },
  { key: 'authCallback', path: WEB_ROUTES.authCallback, access: 'any' },
  { key: 'resetPassword', path: WEB_ROUTES.resetPassword, access: 'guest' },
  { key: 'signin', path: WEB_ROUTES.signin, access: 'guest' },
  { key: 'signinCode', path: WEB_ROUTES.signinCode, access: 'mfa' },
  { key: 'guardianNeeded', path: WEB_ROUTES.guardianNeeded, access: 'minor' },
  { key: 'guardianConsent', path: WEB_ROUTES.guardianConsent, access: 'any' },
  { key: 'guardianWithdraw', path: WEB_ROUTES.guardianWithdraw, access: 'any' },
  { key: 'account', path: WEB_ROUTES.account, access: 'user' },
  { key: 'profile', path: WEB_ROUTES.profile, access: 'user' },
  { key: 'inbox', path: WEB_ROUTES.inbox, access: 'user' },
  { key: 'plans', path: WEB_ROUTES.plans, access: 'user' },
  { key: 'extension', path: WEB_ROUTES.extension, access: 'user' },
  { key: 'security', path: WEB_ROUTES.security, access: 'user' },
  { key: 'data', path: WEB_ROUTES.data, access: 'user' },
  { key: 'privacy', path: WEB_ROUTES.privacy, access: 'any' },
  { key: 'terms', path: WEB_ROUTES.terms, access: 'any' },

  // AI: the AI guide and essay feedback
  { key: 'aiCheck', path: WEB_ROUTES.aiCheck, access: 'any' },
  { key: 'journalTest', path: WEB_ROUTES.journalTest, access: 'any' },

  // reminders: WhatsApp and email
  { key: 'reminders', path: WEB_ROUTES.reminders, access: 'user' },

  // parents: progress page and "parent pays"
  { key: 'family', path: WEB_ROUTES.family, access: 'user' },
  { key: 'familyView', path: WEB_ROUTES.familyView, access: 'any' },
  { key: 'pay', path: WEB_ROUTES.pay, access: 'any' },
  { key: 'payDone', path: WEB_ROUTES.payDone, access: 'any' },
  { key: 'payWithdraw', path: WEB_ROUTES.payWithdraw, access: 'any' },
];

export const NOT_FOUND = { key: 'notFound' as const, path: '', access: 'any' as const };

/* "/account/" and "//account" both mean "/account"; the root stays "/" */
export function normalizePath(pathname: string): string {
  let p = (pathname || '/').split('?')[0].split('#')[0].replace(/\/{2,}/g, '/');
  if (!p.startsWith('/')) p = '/' + p;
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

/* signing up and signing in: one task per screen, at night with the globe behind, like the planner they lead to */
export const NIGHT_PAGES: ReadonlySet<PageKey> = new Set<PageKey>(['start', 'verifyEmail', 'authCallback', 'resetPassword', 'signin', 'signinCode', 'guardianNeeded']);
export const isNightPath = (pathname: string) => NIGHT_PAGES.has(matchRoute(pathname).key);

export function matchRoute(pathname: string): { key: PageKey; path: string; access: Access } {
  const p = normalizePath(pathname);
  return PAGES.find(r => r.path === p) ?? NOT_FOUND;
}

/* The planner (/planner/) and the API are separate apps on the same origin: links to them load a new page. */
export function isAppPath(pathname: string): boolean {
  const p = normalizePath(pathname);
  return !(p === '/planner' || p.startsWith('/planner/') || p === '/api' || p.startsWith('/api/'));
}

const AUTH_PAGES = new Set<string>([WEB_ROUTES.signin, WEB_ROUTES.signinCode, WEB_ROUTES.start, WEB_ROUTES.verifyEmail,
  WEB_ROUTES.authCallback, WEB_ROUTES.resetPassword]);

const isPlannerPath = (p: string) => p === '/planner' || p.startsWith('/planner/');

/* Where to go after signing in: only our own pages, the account's or the planner's (never another site, the API, or
   back into the sign-in flow). */
export function safeNext(next: string | null | undefined, fallback: string = WEB_ROUTES.planner): string {
  if (!next || typeof next !== 'string') return fallback;
  if (!next.startsWith('/') || next.startsWith('//') || next.includes('\\') || /[\u0000-\u001f]/.test(next)) return fallback;
  let url: URL;
  try { url = new URL(next, 'https://app.invalid'); } catch { return fallback; }
  if (url.origin !== 'https://app.invalid') return fallback;
  const path = normalizePath(url.pathname);
  if (path === '/planner') return WEB_ROUTES.planner + url.search + url.hash;   // the planner lives at /planner/
  if (AUTH_PAGES.has(path) || (!isAppPath(path) && !isPlannerPath(path))) return fallback;
  return path + url.search + url.hash;
}

export const signinUrl = (here: string) => `${WEB_ROUTES.signin}?next=${encodeURIComponent(here)}`;
export const codeUrl = (here: string) => `${WEB_ROUTES.signinCode}?next=${encodeURIComponent(here)}`;

/* ---------- session-based access ---------- */
export type Session =
  | { status: 'loading' }
  | { status: 'out' }
  | { status: 'in'; me: Me }
  | { status: 'error' };                       // couldn't reach the API to ask

export const needsCode = (me: Me) => me.mfa.enrolled && me.mfa.aal !== 'aal2';
export const isWaitingMinor = (me: Me) => me.age === 'minor' && !me.can_store;

/* the page a signed-in student belongs on: their plan in the planner (Home), unless a step comes first */
export function homeFor(me: Me): string {
  if (needsCode(me)) return WEB_ROUTES.signinCode;
  if (isWaitingMinor(me)) return WEB_ROUTES.guardianNeeded;
  return WEB_ROUTES.planner;
}

export type Guard = { kind: 'render' } | { kind: 'wait' } | { kind: 'retry' } | { kind: 'redirect'; to: string };

/* here = the current path and query, used as ?next= when the student has to sign in first. app: inside the phone app,
   which opens on the home page: there a signed-in student goes on to their plan, as on any guest page. */
export function guard(access: Access, session: Session, here: string, nextParam?: string | null, opts: { app?: boolean } = {}): Guard {
  if (access === 'landing' && opts.app) access = 'guest';
  if (access === 'any') return { kind: 'render' };
  if (session.status === 'loading') return { kind: 'wait' };
  const open = access === 'guest' || access === 'landing';   // shown to visitors
  if (session.status === 'error') return open ? { kind: 'render' } : { kind: 'retry' };
  if (session.status === 'out') {
    if (open) return { kind: 'render' };
    if (access === 'mfa') return { kind: 'redirect', to: WEB_ROUTES.signin };
    return { kind: 'redirect', to: signinUrl(here) };
  }
  const me = session.me;
  if (needsCode(me)) {
    if (access === 'mfa') return { kind: 'render' };
    if (open) {
      const next = safeNext(nextParam, '');
      return { kind: 'redirect', to: next ? codeUrl(next) : WEB_ROUTES.signinCode };
    }
    return { kind: 'redirect', to: codeUrl(here) };
  }
  if (access === 'mfa' || access === 'guest') {
    const home = homeFor(me);
    return { kind: 'redirect', to: home === WEB_ROUTES.planner ? safeNext(nextParam, home) : home };
  }
  if (access === 'minor') return me.age === 'minor' ? { kind: 'render' } : { kind: 'redirect', to: WEB_ROUTES.account };
  /* user pages and the home page: a minor waiting for a guardian can only use the consent page, privacy, terms and
     sign-out */
  if (isWaitingMinor(me)) return { kind: 'redirect', to: WEB_ROUTES.guardianNeeded };
  return { kind: 'render' };
}
